import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getRequest } from "services/api/apiService";
import { fmtCredits } from "utils/credits";
import {
  splitUsage, bestPack, savingsPct, planCtaLabel,
  monthlyNeed, recommendedPlan, yieldText,
} from "utils/pricingLogic";

// Page tarifs PUBLIQUE (/pricing) — accessible sans compte.
// Consomme /api/pricing/plans, qui est la source de vérité du barème.
const VIDEO_STEPS = [0, 5, 10, 25, 50];

const Check = () => (
  <svg className="w-4 h-4 text-green-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);

const Pricing = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [videos, setVideos] = useState(10);

  useEffect(() => {
    getRequest("/api/pricing/plans").then(setData).catch(() => setError(true));
  }, []);

  const { paid, free } = useMemo(() => splitUsage(data?.usage), [data]);
  const best = useMemo(() => bestPack(data?.packs), [data]);
  const reco = useMemo(() => recommendedPlan(data?.plans, videos), [data, videos]);
  const need = monthlyNeed(videos, 0);

  if (error) return <div className="p-16 text-center text-gray-400">Tarifs momentanément indisponibles.</div>;
  if (!data) return (
    <div className="flex justify-center py-32">
      <div className="w-8 h-8 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-14">

        {/* En-tête */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#221C51]">Des tarifs simples</h1>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto">
            Vous ne payez que la génération créative.
            <span className="text-green-600 font-semibold"> Créer vos publicités et faire de l'A/B testing est gratuit, sans limite.</span>
          </p>
          <p className="text-sm text-gray-400 mt-2">
            1 crédit = 1 vidéo · 0,1 crédit = 1 image · 200 € = 50 vidéos
          </p>
        </div>

        {/* Grille à l'usage */}
        <div className="grid md:grid-cols-2 gap-4 mb-14">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="font-bold text-[#221C51] mb-3">À l'usage</h2>
            <div className="divide-y divide-gray-100">
              {paid.map((u) => (
                <div key={u.key} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium text-[#221C51]">{u.label}</p>
                    <p className="text-xs text-gray-400">{u.note}</p>
                  </div>
                  <span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                    {fmtCredits(u.credits)} cr
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-green-50/60 rounded-2xl border border-green-200 p-6">
            <h2 className="font-bold text-[#221C51] mb-3">Toujours gratuit</h2>
            <div className="divide-y divide-green-100">
              {free.map((u) => (
                <div key={u.key} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium text-[#221C51]">{u.label}</p>
                    <p className="text-xs text-gray-400">{u.note}</p>
                  </div>
                  <span className="text-sm font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">Gratuit</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Simulateur */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-14 text-center">
          <h2 className="font-bold text-[#221C51] mb-1">De combien avez-vous besoin ?</h2>
          <p className="text-xs text-gray-400 mb-4">Vidéos générées par mois</p>
          <div className="flex justify-center gap-2 flex-wrap mb-4">
            {VIDEO_STEPS.map((v) => (
              <button key={v} onClick={() => setVideos(v)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                  videos === v ? "bg-[#221C51] text-white border-[#221C51]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                {v}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500">
            Soit <b className="text-[#221C51]">{fmtCredits(need)} crédits</b> par mois
            {reco && <> — palier conseillé : <b className="text-[#221C51]">{reco.label}</b></>}
          </p>
        </div>

        {/* Paliers */}
        <div className="grid md:grid-cols-4 gap-4 mb-14">
          {(data.plans || []).map((p) => {
            const isReco = reco?.id === p.id;
            return (
              <div key={p.id} className={`bg-white rounded-2xl border p-6 flex flex-col ${
                isReco ? "border-[#221C51] border-2 shadow-lg relative" : "border-gray-200 shadow-sm"}`}>
                {isReco && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#221C51] text-white text-xs font-bold px-3 py-1 rounded-full">
                    Conseillé
                  </span>
                )}
                <h3 className="font-bold text-[#221C51] text-lg">{p.label}</h3>
                <p className="text-3xl font-bold text-[#221C51] mt-2">
                  {p.priceEur === 0 ? "Gratuit" : `${p.priceEur} €`}
                  {p.priceEur > 0 && <span className="text-sm font-normal text-gray-400">/mois</span>}
                </p>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  {p.yield ? yieldText(p.yield) : `${fmtCredits(p.monthlyCredits)} crédits`}
                </p>
                <ul className="space-y-2 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex gap-2 text-xs text-gray-600"><Check /><span>{f}</span></li>
                  ))}
                </ul>
                <Link to="/signup"
                  className={`mt-5 block text-center px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                    isReco ? "bg-[#221C51] text-white hover:bg-[#2e265e]" : "bg-gray-100 text-[#221C51] hover:bg-gray-200"}`}>
                  {planCtaLabel(p.id, null)}
                </Link>
              </div>
            );
          })}
        </div>

        {/* Packs */}
        <h2 className="text-2xl font-bold text-[#221C51] text-center mb-2">Recharges à la carte</h2>
        <p className="text-sm text-gray-400 text-center mb-6">Sans abonnement — les crédits n'expirent pas.</p>
        <div className="grid md:grid-cols-4 gap-4 mb-14">
          {(data.packs || []).map((p) => {
            const save = savingsPct(p, data.packs);
            return (
              <div key={p.id} className={`bg-white rounded-2xl border p-5 text-center ${
                best?.id === p.id ? "border-[#221C51] border-2 shadow-lg" : "border-gray-200 shadow-sm"}`}>
                <div className="flex items-center justify-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-bold text-[#221C51]">{p.label}</h3>
                  {save > 0 && <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">−{save} %</span>}
                </div>
                <p className="text-2xl font-bold text-[#221C51]">{p.priceEur} €</p>
                <p className="text-sm text-gray-600 mt-1 font-medium">{fmtCredits(p.total)} crédits</p>
                <p className="text-xs text-gray-400">
                  {yieldText(p.yield)}
                </p>
                <p className="text-xs text-gray-400 mt-1">{p.pricePerCredit} €/crédit</p>
                {best?.id === p.id && (
                  <p className="text-xs font-bold text-[#221C51] mt-2">Meilleure offre</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Link to="/signup" className="inline-block px-8 py-3 bg-[#221C51] text-white font-bold rounded-xl hover:bg-[#2e265e] shadow-md">
            Commencer gratuitement
          </Link>
          <p className="text-xs text-gray-400 mt-3">Aucune carte requise pour le palier Free.</p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
